import { PASSWORD_MIN, PASSWORD_RULES, passwordSchema, usernameSchema, type PasswordRule } from "@rago/web-shared";
import { z } from "zod";
import { ApiProblemError } from "./authManager";

/**
 * The auth screens' rules, pure and tested: field validators on the SHARED
 * zod schemas (so the client can never drift from what the API accepts),
 * each form's submit check, the password checklist, where a finished
 * sign-in may go, the forgot-mail resend cooldown, and the problem →
 * human-copy map. The routes stay thin and render what this says.
 */

const emailSchema = z.email();

export { PASSWORD_MIN };

/** Undefined when fine, else the line to show under the field. */
export function validateEmail(s: string): string | undefined {
  return emailSchema.safeParse(s.trim()).success ? undefined : "Enter a valid email address.";
}

export function validateUsername(s: string): string | undefined {
  if (usernameSchema.safeParse(s).success) return undefined;
  if (s.length < 3) return "At least 3 characters.";
  if (s.length > 32) return "Up to 32 characters.";
  // Dashes and underscores are allowed, only not in front: the general line
  // would tell "-kenny" to use what it already uses.
  if (/^[-_]/.test(s)) return "Start with a letter or a digit.";
  return "Letters, digits, dashes and underscores only.";
}

export interface ChecklistItem {
  key: PasswordRule;
  label: string;
  ok: boolean;
}

const CHECKLIST: { key: PasswordRule; label: string; need: string }[] = [
  { key: "length", label: `${PASSWORD_MIN} characters`, need: `Needs ${PASSWORD_MIN} characters.` },
  { key: "letter", label: "A letter", need: "Needs a letter." },
  { key: "number", label: "A number", need: "Needs a number." },
  { key: "special", label: "A special character", need: "Needs a special character." },
];

/** Plex's "Password Requirements" rows: every rule, met or not, in display order. */
export function passwordChecklist(password: string): ChecklistItem[] {
  return CHECKLIST.map((c) => ({ key: c.key, label: c.label, ok: PASSWORD_RULES[c.key](password) }));
}

/** Undefined when the shared schema accepts it, else the first unmet rule. */
export function validatePassword(s: string): string | undefined {
  if (passwordSchema.safeParse(s).success) return undefined;
  if (s.length > 200) return "Up to 200 characters.";
  return CHECKLIST.find((c) => !PASSWORD_RULES[c.key](s))?.need;
}

export function validateConfirm(password: string, confirm: string): string | undefined {
  return confirm === password ? undefined : "Passwords don't match.";
}

/** A form's problems at submit, field → the line under it. Keys go in the
 * form's field order, so the first key is the field to focus. Empty = send. */
export type FieldErrors<F extends string> = Partial<Record<F, string>>;

function collect<F extends string>(checks: [F, string | undefined][]): FieldErrors<F> {
  const out: FieldErrors<F> = {};
  for (const [field, error] of checks) if (error) out[field] = error;
  return out;
}

/** Each form's per-field rule — one source for leaving a field, typing in
 * it and submitting the form. */
export const signInChecks = {
  email: validateEmail,
  password: (s: string): string | undefined => (s ? undefined : "Enter your password."),
};

export const signUpChecks = {
  email: validateEmail,
  username: validateUsername,
  password: validatePassword,
};

export function signInErrors(email: string, password: string): FieldErrors<"email" | "password"> {
  return collect<"email" | "password">([
    ["email", signInChecks.email(email)],
    ["password", signInChecks.password(password)],
  ]);
}

export function signUpErrors(
  email: string,
  username: string,
  password: string,
): FieldErrors<"email" | "username" | "password"> {
  return collect<"email" | "username" | "password">([
    ["email", signUpChecks.email(email)],
    ["username", signUpChecks.username(username)],
    ["password", signUpChecks.password(password)],
  ]);
}

/** The first field with a problem — where a failed submit puts the focus. */
export function firstErrorField<F extends string>(errors: FieldErrors<F>): F | undefined {
  return Object.keys(errors)[0] as F | undefined;
}

/**
 * The lines on screen, and whether each is announced (role="alert"): a line
 * that appears as you leave a field, or that the server sent, is; a
 * submit's batch is not — the focus moves to its first field, which reads
 * its own line, rather than firing every alert at once.
 *
 * The rhythm (Kenny, 2026-09-27: "when I click out of a field it should
 * validate it"): leaving a field judges what's in it — an empty one waits
 * for submit, so clicking through doesn't paint the form red; typing only
 * ever takes a line away, the moment the value is right; submit judges all.
 */
export interface ShownError {
  message: string;
  alert: boolean;
  /** The server's verdict on the value typed (a taken username): no local
   * check can clear it — it stays through blur and goes on the first edit. */
  server?: true;
}
export type ShownErrors<F extends string> = Partial<Record<F, ShownError>>;

function without<F extends string>(shown: ShownErrors<F>, field: F): ShownErrors<F> {
  if (!(field in shown)) return shown;
  const rest = { ...shown };
  delete rest[field];
  return rest;
}

/** Leaving `field` holding `value`, which `problem` describes (undefined = fine). */
export function errorsOnBlur<F extends string>(
  shown: ShownErrors<F>,
  field: F,
  value: string,
  problem: string | undefined,
): ShownErrors<F> {
  if (value === "" || shown[field]?.server) return shown;
  if (!problem) return without(shown, field);
  if (shown[field]?.message === problem) return shown;
  return { ...shown, [field]: { message: problem, alert: true } };
}

/** Typing in `field`: its line goes once the value is right (`problem`
 * undefined); a still-wrong value keeps the line it has until blur. A
 * server's line goes on any edit — it judged the old value. */
export function errorsOnType<F extends string>(
  shown: ShownErrors<F>,
  field: F,
  problem: string | undefined,
): ShownErrors<F> {
  return problem && !shown[field]?.server ? shown : without(shown, field);
}

/** A submit's findings, all at once and unannounced. */
export function errorsOnSubmit<F extends string>(found: FieldErrors<F>): ShownErrors<F> {
  const out: ShownErrors<F> = {};
  for (const field of Object.keys(found) as F[]) out[field] = { message: found[field]!, alert: false };
  return out;
}

/** The server's word on one field (a signup 409): shown and announced. */
export function errorFromServer<F extends string>(field: F, message: string): ShownErrors<F> {
  return { [field]: { message, alert: true, server: true } } as ShownErrors<F>;
}

/**
 * Where a finished sign-in may go: this app's own path only. `next` comes
 * from the address bar, and the router follows an absolute URL out of the
 * app (a document navigation), so `?next=https://…` would hand a freshly
 * signed-in user to any site. Every real `next` is a path — the page the
 * signed-out user asked for, the /link approval, a verify link. No `//` or
 * `/\` (protocol-relative), no backslash or control character anywhere
 * (the URL parser drops tabs and newlines, so `/\t/x` would become `//x`).
 */
export function safeNext(next: string | undefined): string | undefined {
  if (!next || !next.startsWith("/")) return undefined;
  if (next[1] === "/" || /[\\\p{Cc}]/u.test(next)) return undefined;
  return next;
}

/** The navigation to `next` (else Home): a query rides in `href` — `to`
 * would drop it (a /link code, a verify token). */
export function nextTarget(next: string | undefined): { href: string } | { to: string } {
  const target = safeNext(next) ?? "/";
  return target.includes("?") ? { href: target } : { to: target };
}

/** How long "Resend link" rests after a resend (the API allows 5 per 15 min). */
export const RESEND_COOLDOWN_MS = 60_000;

export type AuthContext = "signin" | "signup" | "forgot" | "reset" | "verify" | "resend";

export interface AuthErrorInfo {
  message: string;
  /** A signup 409 that belongs under one field rather than at the top. */
  field?: "email" | "username";
  /** The API refused what was typed (wrong email or password): the fields
   * read as invalid — unlike a network failure or a rate limit. */
  invalid?: true;
}

/** The API's problem, in the screen's own words and with a way out. */
export function describeAuthError(err: unknown, ctx: AuthContext): AuthErrorInfo {
  if (!(err instanceof ApiProblemError)) {
    return { message: "Can't reach Rago right now. Check your connection and try again." };
  }
  const p = err.problem;
  if (p.status === 429 || (p.code as string) === "rate_limited") {
    return {
      message:
        ctx === "resend"
          ? "You've asked a few times already. Wait 5 minutes and try again."
          : ctx === "forgot"
            ? "Too many attempts. Wait 15 minutes and try again."
            : "Too many attempts. Wait a few minutes and try again.",
    };
  }
  if (p.code === "invalid_credentials") {
    return { message: "Wrong email or password.", invalid: true };
  }
  if (ctx === "signup" && p.status === 409) {
    if (/email/i.test(p.title)) return { message: "That email already has an account.", field: "email" };
    if (/username/i.test(p.title)) return { message: "That username is taken.", field: "username" };
  }
  if ((ctx === "reset" || ctx === "verify") && p.status === 400) {
    return { message: "This link is invalid or has expired. Request a new one." };
  }
  return { message: p.detail ?? p.title };
}
